// utils/feeIntent.ts
//
// FEE-RATE-SEMANTICS-1 Phase 2B -- posting fee-intent discriminator shared by
// SaleEntry (POST /sales) and SaleDetail (POST /ar-ap/customer-payments).
//
// Condition 4b (binding): the posted payload must be TYPED (never `any`), and
// the discriminated union must reject `configured` + a fee and `explicit`
// without a fee at COMPILE TIME (proven by feeIntent.typecheck.ts). This
// mirrors the backend contract (backend/utils/fee_mode.py
// recognized_mode_shape_error): configured forbids any fee incl. 0; explicit
// requires a present non-null fee (0 valid).

// Strict discriminated union: configured forbids a fee key (fee?: never);
// explicit requires a numeric fee (0 valid). feeIntent.typecheck.ts proves both
// invalid shapes are rejected at compile time.
export type FeeIntent =
  | { fee_mode: "configured"; fee?: never }
  | { fee_mode: "explicit"; fee: number };

// Payload fee-fields, including the no-fee-relevant-payment arm. Deliberately
// NOT `{}` (which accepts almost any object) -- the third arm explicitly forbids
// both keys so a no-payment body cannot smuggle a stray fee.
export type FeePayloadFields =
  | { fee_mode: "configured"; fee?: never }
  | { fee_mode: "explicit"; fee: number }
  | { fee_mode?: never; fee?: never };

/**
 * Resolve the posting fee-intent for a payment surface.
 *
 * - no positive offline payment                 -> null (attach neither field)
 * - cash                                        -> explicit / 0
 * - explicit (manual override / user-entered)   -> explicit / feeValue (validated)
 * - otherwise (automatic noncash)               -> configured (no fee; backend computes)
 *
 * `feeValue` is only consulted for the explicit non-cash arm and must already
 * be a finite, non-negative number there (callers validate via parseFeeOverride
 * / their strict Fee parse). A null/invalid feeValue in that arm is a caller
 * bug and throws loudly rather than silently posting a wrong or absent fee.
 */
export function buildFeeIntent(args: {
  hasPositiveOfflinePayment: boolean;
  isCash: boolean;
  explicit: boolean;
  feeValue: number | null;
}): FeeIntent | null {
  if (!args.hasPositiveOfflinePayment) return null;
  if (args.isCash) return { fee_mode: "explicit", fee: 0 };
  if (args.explicit) {
    if (
      args.feeValue === null ||
      !Number.isFinite(args.feeValue) ||
      args.feeValue < 0
    ) {
      throw new Error(
        "buildFeeIntent: explicit fee must be a finite, non-negative number",
      );
    }
    return { fee_mode: "explicit", fee: args.feeValue };
  }
  return { fee_mode: "configured" };
}
